/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { ToastType } from "./use-toast-types";

const TOAST_COLORS: Record<ToastType, string> = {
  success: "green",
  error: "red",
  warning: "yellow",
  info: "blue",
};

/**
 * Gets color for toast type.
 * 
 * @param {ToastType} type - Toast type
 * @returns {string} Color string
 */
export function getToastColor(type: ToastType): string {
  return TOAST_COLORS[type] || "blue";
}

/**
 * Gets translation key for toast type.
 * 
 * @param {ToastType} type - Toast type
 * @returns {object} Translation key and options
 */
function getToastTranslationKey(type: ToastType): { key: string; options?: any } {
  const translations: Record<ToastType, { key: string; options?: any }> = {
    success: { key: 'messages.success.title', options: { defaultValue: 'Success' } },
    error: { key: 'messages.error.errorTitle' },
    warning: { key: 'messages.warning.title', options: { defaultValue: 'Warning' } },
    info: { key: 'messages.info.title', options: { defaultValue: 'Information' } },
  };
  return translations[type] || { key: 'messages.info.title', options: { defaultValue: 'Notification' } };
}

/**
 * Gets default title for toast type.
 * 
 * @param {ToastType} type - Toast type
 * @param {Function} t - Translation function
 * @returns {string} Default title
 */
export function getDefaultTitle(type: ToastType, t: (key: string, options?: any) => string): string {
  const translation = getToastTranslationKey(type);
  return t(translation.key, translation.options);
}

/**
 * Gets toast position based on device type.
 * 
 * @param {boolean} isMobile - Whether on mobile device
 * @returns {string} Toast position
 */
export function getToastPosition(isMobile: boolean): string {
  return isMobile ? "bottom" : "top-right";
}

export { getToastMinHeight, getToastStyles } from "./use-toast-styles";

/**
 * Calculates appropriate toast duration based on message length.
 * Ensures users have sufficient time to read the message.
 * 
 * @param {string} message - Message text
 * @param {number} customDuration - Optional custom duration override
 * @returns {number} Duration in milliseconds
 */
export function calculateDuration(message: string, customDuration?: number): number {
  if (customDuration !== undefined) {
    return customDuration;
  }
  // Minimum 10 seconds for reading, ~150ms per character
  const baseDuration = 10000;
  const messageLength = message.length;
  return Math.max(baseDuration, messageLength * 150);
}
